// src/pages/Order/Purchase Order/Purchase Order Entry/purchaseOrderSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPurchaseOrderNumber,
  createPurchaseOrder,
  fetchPrediction,
} from "./purchaseOrderThunk";
import { currentDateTime } from "../../../../lib/functions";
import Big from "big.js";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

const initialState = {
  // Supplier & Items
  supplierInfo: {},
  itemCart: [],
  payServiceCart: [],
  predictionMap: {},

  // Footer values
  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    total_transport_cost: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    serviceCharge: 0,
    discountPct: 0,
    discountAmt: 0,
    taxPct: 0,
    paymentTermsDescription: "",
    showTermsConditions: true,
  },

  // Extra masterData fields
  tagFieldId: null,
  selectedDiscountAcc: null,
  selectedTaxAcc: null,
  selectedTransportAcc: null,
  conversionRate: 0,
  conversionCurrency: null,

  // System values
  voucherNumber: null,
  pur_o_id: null,
  entryDate: currentDateTime(),
  dueDate: null,
  paymentTerm: "Immediate",

  selectedOrderDetails: null,
  loading: false,
  loadingEdit: false,
  loadingFetch: false,
  success: null,
  error: null,
  errorEdit: null,
  errorFetch: null,
  taxMode: "on_item",
};

const purchaseOrderSlice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    resetPurchaseOrder() {
      return { ...initialState };
    },

    // Supplier
    setSupplier(state, action) {
      state.supplierInfo = action.payload || {};
    },

    // Items
    addOrderRow(state, action) {
      state.itemCart.push(action.payload);
    },
    // deleteOrderRow(state, action) {
    //   state.itemCart = state.itemCart.filter((r) => r.key !== action.payload);
    // },
    deleteOrderRow(state, action) {
      state.itemCart =
        state.itemCart.filter((r) => r.key !== action.payload);

      // 🔥 Recalculate totals
      let subtotal = new Big(0);
      let totalDiscount = new Big(0);
      let totalTax = new Big(0);

      state.itemCart.forEach((it) => {
        if (it.bonus) return;

        const qty = Number(it.quantity) || 0;
        const rate = Number(it.rate) || 0;
        let line = new Big(qty).times(rate);

        if (state.taxMode === "on_item") {
          const discountAmt = getItemDiscountAmount(it, line);
          const afterDiscount = line.minus(discountAmt);

          const taxPer = Number(it.tax_per) || 0;
          const taxAmt = afterDiscount.times(taxPer).div(100);

          subtotal = subtotal.plus(afterDiscount).plus(taxAmt);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
        } else {
          subtotal = subtotal.plus(line);
        }
      });

      const expenseTotal =
        (state.footerValues.payServiceCart || [])
          .reduce((sum, r) => sum.plus(r.tran_amount || 0), new Big(0));

      const grandTotal = subtotal.plus(expenseTotal);

      state.footerValues.subtotal = subtotal.toFixed(2);
      state.footerValues.grandTotal = grandTotal.toFixed(2);
    },
    updateOrderRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.itemCart = state.itemCart.map((row) =>
        row.key === rowKey ? { ...row, ...updates } : row
      );
    },

    // Pay Service Cart
    setPayServiceCart(state, action) {
      state.payServiceCart = action.payload || [];
    },

    // Tag
    setTagFieldId(state, action) {
      state.tagFieldId = action.payload || null;
    },

    // Accounts
    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },
    setTransportAcc(state, action) {
      state.selectedTransportAcc = action.payload;
    },

    // Conversion
    setConversionRate(state, action) {
      state.conversionRate = action.payload;
    },
    setConversion(state, action) {
      state.conversionCurrency = action.payload || null;
    },

    // Dates
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setDueDate(state, action) {
      state.dueDate = action.payload;
    },

    // Tax Mode
    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },

    // Generic Formfield
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },

    setFooterValues(state, action) {
      state.footerValues = {
        ...state.footerValues,
        ...action.payload,
      };
    },

    setPurchaseOrderId(state, action) {
      state.pur_o_id = action.payload;
    },
    setPurchaseOrderNo(state, action) {
      state.voucherNumber = action.payload;
    },


    setEditModeData(state, action) {
      const data = action.payload;

      // Set all fields from transformed data
      if (data.supplierInfo) state.supplierInfo = data.supplierInfo;
      if (data.itemCart) state.itemCart = data.itemCart;
      if (data.footerValues) state.footerValues = data.footerValues;
      if (data.narration) state.narration = data.narration;
      if (data.voucherNumber) state.voucherNumber = data.voucherNumber;
      if (data.entryDate) state.entryDate = data.entryDate;
      if (data.dueDate) state.dueDate = data.dueDate;
      if (data.tagFieldId) state.tagFieldId = data.tagFieldId;
      if (data.pur_o_id) state.pur_o_id = data.pur_o_id;
      if (data.paymentTerm) state.paymentTerm = data.paymentTerm;
      if (data.taxMode) state.taxMode = data.taxMode;
      if (data.selectedDiscountAcc) state.selectedDiscountAcc = data.selectedDiscountAcc;
      if (data.selectedTaxAcc) state.selectedTaxAcc = data.selectedTaxAcc;
      if (data.selectedTransportAcc) state.selectedTransportAcc = data.selectedTransportAcc;
      if (data.conversionRate) state.conversionRate = data.conversionRate;
      if (data.conversionCurrency) state.conversionCurrency = data.conversionCurrency;

      // Clear errors
      state.error = null;
      state.errorEdit = null;
      state.errorFetch = null;
    },

    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      const isIndia = authInfo?.country?.toLowerCase() === "india";
      const gstEnabled = authInfo?.gst_enabled === "yes";
      const showTax = !isIndia || gstEnabled;
      let subtotal = new Big(0);
      let totalDiscount = new Big(0);
      let totalTax = new Big(0);
      let finalSubTotal = 0;

      state.itemCart.forEach((it) => {
        const qty = Number(it.quantity) || 0;
        const rate = Number(it.rate) || 0;

        // Skip bonus items (if purchase also has bonus items)
        if (it.bonus) return;

        let line = new Big(qty).times(rate);

        if (state.taxMode === "on_item") {
          const discountAmt = getItemDiscountAmount(it, line);
          const afterDiscount = line.minus(discountAmt);
          line = line.minus(discountAmt);

          const taxPer = showTax ? Number(it.tax_per) || 0 : 0;
          const taxAmt = line.times(taxPer).div(100);
          line = line.plus(taxAmt);

          subtotal = subtotal.plus(line);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
        } else {
          subtotal = subtotal.plus(line);
        }
      });

      let resolvedDiscountPct = state.footerValues.discountPct;

      if (state.taxMode === "on_total") {
        const footerDiscountMode = state.footerValues.discountMode === "amount" ? "amount" : "percent";
        if (footerDiscountMode === "amount") {
          const raw = new Big(state.footerValues.discountAmt || 0);
          totalDiscount = raw.gt(subtotal) ? subtotal : raw.lt(0) ? new Big(0) : raw;
          // Back-computed so the printed voucher (which reads this field
          // directly) is correct even when the footer is in amount mode.
          resolvedDiscountPct = subtotal.eq(0) ? 0 : Number(totalDiscount.div(subtotal).times(100).toFixed(13));
        } else {
          const discountPct = Number(state.footerValues.discountPct) || 0;
          totalDiscount = subtotal.times(discountPct).div(100);
          resolvedDiscountPct = discountPct;
        }

        const afterDiscount = subtotal.minus(totalDiscount);

        const authTax = showTax ? Number(authInfo?.item_tax) || 0 : 0;
        totalTax = afterDiscount.times(authTax).div(100);
      }

      // const serviceCharge = Number(state.footerValues.serviceCharge) || 0;
      const serviceCharge = new Big(0);
      const expenseTotal = (state.footerValues.payServiceCart || []).reduce((sum, r) => sum.plus(r.tran_amount || 0), new Big(0));
      // const grandTotal = subtotal - totalDiscount + totalTax + serviceCharge;

      const grandTotal = state.taxMode === "on_item"
      ? subtotal.plus(serviceCharge).plus(expenseTotal)
      : subtotal.minus(totalDiscount).plus(totalTax).plus(expenseTotal).plus(serviceCharge);

      state.footerValues = {
        ...state.footerValues,
        subtotal: subtotal.toFixed(2),
        total_discount: totalDiscount.toFixed(2),
        discountPct: resolvedDiscountPct,
        totalTax: totalTax.toFixed(2),
        serviceCharge: serviceCharge.toFixed(2),
        grandTotal: grandTotal.toFixed(2),
        dueAmount: grandTotal
        .minus(state.footerValues.paidAmount || 0)
        .toFixed(2),
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseOrderNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(fetchPrediction.fulfilled, (state, action) => {
        const itemId = action.meta.arg;
        state.predictionMap[itemId] = action.payload;
      })
      .addCase(createPurchaseOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPurchaseOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createPurchaseOrder.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetPurchaseOrder,
  setSupplier,
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setPayServiceCart,
  setTagFieldId,
  setDiscountAcc,
  setTaxAcc,
  setTransportAcc,
  setConversion,
  setEntryDate,
  setDueDate,
  setFormField,
  setFooterValues,
  setTaxMode,
  setConversionRate,
  setPurchaseOrderId,
  setEditModeData,
  setPurchaseOrderNo
} = purchaseOrderSlice.actions;

export default purchaseOrderSlice.reducer;
